/**
 * Event Flow reports listing page.
 */
import { GitBranch, Calendar, Filter, Repeat, Target, Play } from 'lucide-react';
import { ReportsPage } from '@/components/reports/ReportsPage';
import { ConfigBadge } from '@/components/reports/ConfigBadge';
import { PRESET_LABELS } from '@/lib/reports.types';
import { countFilters } from '@/lib/filters';

const iconCls = 'w-3 h-3 shrink-0';

function renderBadges(config: Record<string, unknown>) {
  const preset = (config.preset as string) || '';
  const startDate = (config.start as string) || (config.startDate as string) || '';
  const endDate = (config.end as string) || (config.endDate as string) || '';
  const filterMode = (config.filterMode as string) || 'exclude';
  const events = (config.events as string[]) || [];
  const collapse = Boolean(config.collapse || config.collapseRepeats);
  const kpi = (config.kpi as string) || (config.kpiEvent as string) || '';
  const startFrom = (config.startFrom as string) || (config.startFromEvent as string) || '';
  const filterCount = countFilters(config.filters);

  const dateLabel = preset
    ? PRESET_LABELS[preset] || preset.replace(/_/g, ' ')
    : startDate && endDate
      ? `${startDate} → ${endDate}`
      : '';

  return (
    <>
      {dateLabel && <ConfigBadge icon={<Calendar className={iconCls} />} text={dateLabel} color="amber" />}
      {events.length > 0 && (
        <ConfigBadge
          icon={<Play className={iconCls} />}
          text={`${filterMode === 'include' ? 'Include' : 'Exclude'}: ${events.length} event${events.length > 1 ? 's' : ''}`}
          color="sky"
        />
      )}
      {collapse && <ConfigBadge icon={<Repeat className={iconCls} />} text="Collapse" color="slate" />}
      {kpi && <ConfigBadge icon={<Target className={iconCls} />} text={`KPI: ${kpi}`} color="violet" />}
      {startFrom && <ConfigBadge icon={<Play className={iconCls} />} text={`From: ${startFrom}`} color="emerald" />}
      {filterCount > 0 && <ConfigBadge icon={<Filter className={iconCls} />} text={`${filterCount} filter${filterCount > 1 ? 's' : ''}`} color="rose" />}
    </>
  );
}

export function EventFlowReports() {
  return (
    <ReportsPage
      sectionId="event_flow"
      sectionRoute="/event-flow"
      title="Event Flow"
      subtitle="Visualize user event sequences as interactive flow trees"
      emptyIcon={GitBranch}
      emptyDescription="Create your first event flow report to start exploring user journeys"
      showFolders
      accentColor="sky"
      cardIcon={<GitBranch className="w-4 h-4 text-cyan-500" />}
      cardIconBgClass="bg-cyan-50"
      renderBadges={renderBadges}
    />
  );
}
